import { useState, useEffect, useMemo, useRef } from 'react';
import {
  CalendarDays,
  Plus,
  Edit,
  Archive,
  MapPin,
  Clock,
  Users,
  Link2,
  AlertCircle,
  Loader2,
  Save,
  Star,
  Filter,
  Search,
  X,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { formatEventDateRange } from '@/hooks/useActiveEvent';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { DesktopShell } from '@/components/desktop/DesktopShell';
import { ThreePanelLayout } from '@/components/desktop/layouts/ThreePanelLayout';

// ─── Types ────────────────────────────────────────────────────────────────────

type EventStatus = 'active' | 'archived';

interface NpsEvent {
  id: string;
  name: string;
  dateStart: string;
  dateEnd: string;
  location: string;
  welcomeMessage: string | null;
  status: EventStatus;
  exhibitorRegistrationDeadline: string | null;
  npsProjectRegistrationDeadline: string | null;
  attendeeRegistrationDeadline: string | null;
  sessionCount: number;
  connectionCount: number;
  projectCount: number;
  partnerCount: number;
  conversationCount: number;
  createdAt: string;
  updatedAt: string;
  attendeeCount?: number;
}


interface EventFormData {
  name: string;
  dateStart: string;
  dateEnd: string;
  location: string;
  welcomeMessage: string;
  status: EventStatus;
  // Deadlines split into date + time inputs (matching the Start/End style).
  // Empty date = no deadline. Empty time defaults to 23:59 (end of day) on save.
  exhibitorDeadlineDate: string;
  exhibitorDeadlineTime: string;
  npsProjectDeadlineDate: string;
  npsProjectDeadlineTime: string;
  attendeeDeadlineDate: string;
  attendeeDeadlineTime: string;
}

const DEFAULT_FORM: EventFormData = {
  name: '',
  dateStart: '',
  dateEnd: '',
  location: '',
  welcomeMessage: '',
  status: 'active',
  exhibitorDeadlineDate: '',
  exhibitorDeadlineTime: '',
  npsProjectDeadlineDate: '',
  npsProjectDeadlineTime: '',
  attendeeDeadlineDate: '',
  attendeeDeadlineTime: '',
};

const ALL_STATUSES: EventStatus[] = ['active', 'archived'];

// ─── Helpers ──────────────────────────────────────────────────────────────────


const STATUS_BADGE: Record<EventStatus, string> = {
  active: 'bg-green-100 text-green-800 border-green-200',
  archived: 'bg-muted text-muted-foreground border-border',
};


function toLocalDateValue(iso: string) {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10); // YYYY-MM-DD
}

// Split an ISO timestamp into local-time YYYY-MM-DD and HH:mm parts.
function splitIsoToLocalParts(iso: string | null | undefined): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { date: '', time: '' };
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

// Combine a YYYY-MM-DD date with an optional HH:mm time (admin's local timezone) into a UTC ISO string.
// Empty date → null (no deadline). Empty time defaults to end-of-day (23:59:59.999).
function combineDateTimeToIso(localDate: string, localTime: string): string | null {
  if (!localDate) return null;
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(localDate);
  if (!dateMatch) return null;
  const year = Number(dateMatch[1]);
  const month = Number(dateMatch[2]);
  const day = Number(dateMatch[3]);

  let hour = 23, minute = 59, second = 59, ms = 999;
  if (localTime) {
    const timeMatch = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(localTime);
    if (timeMatch) {
      hour = Number(timeMatch[1]);
      minute = Number(timeMatch[2]);
      second = timeMatch[3] ? Number(timeMatch[3]) : 0;
      ms = 0;
    }
  }
  const date = new Date(year, month - 1, day, hour, minute, second, ms);
  return isNaN(date.getTime()) ? null : date.toISOString();
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function EventManagementPage() {
  const [events, setEvents] = useState<NpsEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [eventDetail, setEventDetail] = useState<NpsEvent | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filter state
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatuses, setFilterStatuses] = useState<EventStatus[]>([]);


  // Form modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editEventId, setEditEventId] = useState<string | null>(null);
  const [formData, setFormData] = useState<EventFormData>(DEFAULT_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Archive-and-proceed (auto-archive existing active event when saving a new active event)
  const [archiveAndProceedId, setArchiveAndProceedId] = useState<string | null>(null);
  const [pendingSubmitPayload, setPendingSubmitPayload] = useState<Record<string, unknown> | null>(null);
  const [archiveAndProceedLoading, setArchiveAndProceedLoading] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  // Auto-select the active event on first load only.
  // A ref guard ensures this fires exactly once after events arrive — otherwise
  // it fights the "clear selection on filter" effect and produces a re-render loop
  // when the user filters out the currently-selected event.
  const didAutoSelectRef = useRef(false);
  useEffect(() => {
    if (didAutoSelectRef.current || events.length === 0) return;
    didAutoSelectRef.current = true;
    const active = events.find((ev) => ev.status === 'active');
    if (active) setSelectedEventId(active.id);
  }, [events]);

  useEffect(() => {
    if (selectedEventId) {
      fetchEventDetail(selectedEventId);
    } else {
      setEventDetail(null);
    }
  }, [selectedEventId]);

  const fetchEvents = async () => {
    try {
      const res = await api.get<{ success: boolean; data: NpsEvent[] }>('/admin/events');
      setEvents(res.data ?? []);
    } catch {
      toast.error('Failed to load events');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEventDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await api.get<{ success: boolean; data: NpsEvent }>(`/admin/events/${id}`);
      setEventDetail(res.data ?? null);
    } catch {
      toast.error('Failed to load event details');
    } finally {
      setDetailLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditEventId(null);
    setFormData(DEFAULT_FORM);
    setFormError(null);
    setModalOpen(true);
  };

  const openEditModal = (event: NpsEvent) => {
    setEditEventId(event.id);
    setFormError(null);
    setFormLoading(true);
    setModalOpen(true);
    const exhibitorParts = splitIsoToLocalParts(event.exhibitorRegistrationDeadline);
    const npsParts = splitIsoToLocalParts(event.npsProjectRegistrationDeadline);
    const attendeeParts = splitIsoToLocalParts(event.attendeeRegistrationDeadline);
    setFormData({
      name: event.name,
      dateStart: toLocalDateValue(event.dateStart),
      dateEnd: toLocalDateValue(event.dateEnd),
      location: event.location,
      welcomeMessage: event.welcomeMessage ?? '',
      status: event.status,
      exhibitorDeadlineDate: exhibitorParts.date,
      exhibitorDeadlineTime: exhibitorParts.time,
      npsProjectDeadlineDate: npsParts.date,
      npsProjectDeadlineTime: npsParts.time,
      attendeeDeadlineDate: attendeeParts.date,
      attendeeDeadlineTime: attendeeParts.time,
    });
    setFormLoading(false);
  };

  const buildPayload = () => {
    const {
      exhibitorDeadlineDate,
      exhibitorDeadlineTime,
      npsProjectDeadlineDate,
      npsProjectDeadlineTime,
      attendeeDeadlineDate,
      attendeeDeadlineTime,
      ...rest
    } = formData;
    return {
      ...rest,
      // date inputs give YYYY-MM-DD; convert to ISO datetime for the API
      dateStart: formData.dateStart ? new Date(formData.dateStart + 'T00:00:00.000Z').toISOString() : '',
      dateEnd: formData.dateEnd ? new Date(formData.dateEnd + 'T00:00:00.000Z').toISOString() : '',
      // Deadlines pair a local date with an optional time (default 23:59 if blank).
      // null clears a previously-set deadline; the API treats null/missing as "no deadline".
      exhibitorRegistrationDeadline: combineDateTimeToIso(exhibitorDeadlineDate, exhibitorDeadlineTime),
      npsProjectRegistrationDeadline: combineDateTimeToIso(npsProjectDeadlineDate, npsProjectDeadlineTime),
      attendeeRegistrationDeadline: combineDateTimeToIso(attendeeDeadlineDate, attendeeDeadlineTime),
    };
  };

  const doSubmit = async (payload: Record<string, unknown>) => {
    setFormSaving(true);
    try {
      if (editEventId) {
        await api.patch(`/admin/events/${editEventId}`, payload);
        toast.success('Event updated');
      } else {
        await api.post('/admin/events', payload);
        toast.success('Event created');
      }
      setModalOpen(false);
      fetchEvents();
      if (selectedEventId) fetchEventDetail(selectedEventId);
    } catch (error: any) {
      const msg = error.response?.data?.error ?? `Failed to ${editEventId ? 'update' : 'create'} event`;
      setFormError(msg);
      toast.error(msg);
    } finally {
      setFormSaving(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const payload = buildPayload();

    // If activating this event, check if another event is already active
    if (formData.status === 'active') {
      const existingActive = events.find((ev) => ev.status === 'active' && ev.id !== editEventId);
      if (existingActive) {
        setPendingSubmitPayload(payload);
        setArchiveAndProceedId(existingActive.id);
        return; // wait for confirmation
      }
    }

    await doSubmit(payload);
  };

  const handleArchiveAndProceed = async () => {
    if (!archiveAndProceedId || !pendingSubmitPayload) return;
    setArchiveAndProceedLoading(true);
    try {
      await api.post(`/admin/events/${archiveAndProceedId}/archive`);
      toast.success(`Event archived`);
      setArchiveAndProceedId(null);
      setPendingSubmitPayload(null);
      await doSubmit(pendingSubmitPayload);
    } catch (error: any) {
      toast.error(error.response?.data?.error ?? 'Failed to archive existing event');
    } finally {
      setArchiveAndProceedLoading(false);
    }
  };

  const toggleStatus = (status: EventStatus) =>
    setFilterStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );

  const clearFilters = () => {
    setFilterSearch('');
    setFilterStatuses([]);
  };

  const filteredEvents = useMemo(() => {
    return events.filter((ev) => {
      if (filterSearch) {
        const q = filterSearch.toLowerCase();
        if (
          !ev.name.toLowerCase().includes(q) &&
          !ev.location.toLowerCase().includes(q)
        ) return false;
      }
      if (filterStatuses.length > 0 && !filterStatuses.includes(ev.status)) return false;
      return true;
    });
  }, [events, filterSearch, filterStatuses]);

  // Clear selection when it's no longer in the visible list
  useEffect(() => {
    if (selectedEventId && !filteredEvents.some((e) => e.id === selectedEventId)) {
      setSelectedEventId(null);
    }
  }, [filteredEvents, selectedEventId]);

  const activeFilterCount = (filterSearch ? 1 : 0) + filterStatuses.length;
  const selectedEvent = filteredEvents.find((e) => e.id === selectedEventId) ?? null;

  // ─── Left Panel: Filters ──────────────────────────────────────────────────

  const FiltersPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Filter className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Filters</h2>
            <p className="text-sm text-muted-foreground">Refine your search</p>
          </div>
        </div>
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs">
            Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Search */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Name or location..."
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                className="pl-9 h-9"
              />
              {filterSearch && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setFilterSearch('')}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          <Separator />

          {/* Status Filter */}
          <div>
            <Label className="text-sm font-medium mb-3 block">Status</Label>
            <div className="space-y-2">
              {ALL_STATUSES.map((status) => (
                <div key={status} className="flex items-center gap-2">
                  <Checkbox
                    id={`status-${status}`}
                    checked={filterStatuses.includes(status)}
                    onCheckedChange={() => toggleStatus(status)}
                  />
                  <label
                    htmlFor={`status-${status}`}
                    className="text-sm cursor-pointer flex items-center gap-2"
                  >
                    <Badge variant="outline" className={cn('capitalize w-20 justify-center px-2.5 py-0.5 text-xs font-medium', STATUS_BADGE[status])}>
                      {status}
                    </Badge>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          <Card className="border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Info className="h-4 w-4 text-primary" />
                Event Tagging
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground leading-relaxed">
                When a new event is created, it becomes the active event. All new registrations, sessions, and connections are automatically tagged to the active event until a new one is created.
              </p>
            </CardContent>
          </Card>
        </div>
      </ScrollArea>
    </div>
  );

  // ─── Center Panel: Event List ─────────────────────────────────────────────

  const EventRowSkeleton = () => (
    <tr>
      <td className="px-4 py-3"><Skeleton className="h-4 w-40" /></td>
      <td className="px-4 py-3"><Skeleton className="h-4 w-32" /></td>
      <td className="px-4 py-3"><Skeleton className="h-4 w-28" /></td>
      <td className="px-4 py-3"><Skeleton className="h-5 w-20 rounded-full" /></td>
      <td className="px-4 py-3 text-right">
        <div className="flex justify-end gap-2">
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-8 w-8" />
        </div>
      </td>
    </tr>
  );

  const CenterPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center justify-between border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <CalendarDays className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Events</h2>
            <p className="text-sm text-muted-foreground">
              {isLoading ? 'Loading...' : `${filteredEvents.length} of ${events.length} event${events.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" className="gap-1.5" onClick={openCreateModal}>
            <Plus className="h-4 w-4" />
            New Event
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <table className="w-full">
            <tbody className="divide-y divide-border">
              {Array.from({ length: 4 }).map((_, i) => <EventRowSkeleton key={i} />)}
            </tbody>
          </table>
        ) : filteredEvents.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium">No events found</p>
            {events.length > 0 && (
              <p className="text-sm mt-1">Try adjusting your filters</p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredEvents.map((event) => {
              const isArchived = event.status === 'archived';
              return (
                <div
                  key={event.id}
                  onClick={() => setSelectedEventId(event.id === selectedEventId ? null : event.id)}
                  className={cn(
                    'px-4 py-3 cursor-pointer transition-colors hover:bg-muted/50 flex items-center justify-between gap-3',
                    selectedEventId === event.id && 'bg-primary/5 border-l-2 border-l-primary',
                  )}
                >
                  <div className={cn('flex-1 min-w-0', isArchived && 'opacity-60')}>
                    <div className="text-sm font-medium text-foreground truncate">{event.name}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className={cn('capitalize w-20 justify-center px-2.5 py-0.5 text-xs font-medium', STATUS_BADGE[event.status])}>
                      {event.status}
                    </Badge>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => openEditModal(event)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Edit event"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ─── Right Panel: Event Detail + Reports ──────────────────────────────────

  const RightPanel = (
    <div className="h-full flex flex-col">
      <div className="h-[72px] px-4 flex items-center border-b border-border bg-muted flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Star className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-semibold text-lg">Event Details</h2>
            <p className="text-sm text-muted-foreground">
              {selectedEvent ? selectedEvent.name : 'Select an event'}
            </p>
          </div>
        </div>
      </div>

      {!selectedEventId ? (
        <div className="flex-1 flex items-center justify-center text-muted-foreground p-8">
          <div className="text-center">
            <CalendarDays className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">Select an event</p>
            <p className="text-sm mt-1">Click any row to view full details</p>
          </div>
        </div>
      ) : detailLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : eventDetail ? (
        <ScrollArea className="flex-1">
          <div className="p-5 space-y-5">
            {/* Archived banner */}
            {eventDetail.status === 'archived' && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-muted border border-border">
                <Archive className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  This event is archived and read-only. Sessions and connections belonging to it cannot be modified.
                </p>
              </div>
            )}

            {/* Header */}
            <div>
              <h3 className="text-xl font-bold">{eventDetail.name}</h3>
              <div className="flex items-center gap-2 mt-1.5">
                <Badge variant="outline" className={cn('capitalize w-20 justify-center px-2.5 py-0.5 text-xs font-medium', STATUS_BADGE[eventDetail.status])}>
                  {eventDetail.status}
                </Badge>
              </div>
            </div>

                {/* Metadata */}
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 shrink-0" />
                    <span>{formatEventDateRange(eventDetail.dateStart, eventDetail.dateEnd)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 shrink-0" />
                    <span>{eventDetail.location}</span>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg bg-muted/60 p-3 text-center">
                    <p className="text-xl font-bold">{eventDetail.attendeeCount ?? '—'}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center justify-center gap-1">
                      <Users className="h-3 w-3" /> Attendees
                    </p>
                  </div>
                  <div className="rounded-lg bg-muted/60 p-3 text-center">
                    <p className="text-xl font-bold">{eventDetail.sessionCount}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center justify-center gap-1">
                      <CalendarDays className="h-3 w-3" /> Sessions
                    </p>
                  </div>
                  <div className="rounded-lg bg-muted/60 p-3 text-center">
                    <p className="text-xl font-bold">{eventDetail.connectionCount}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 flex items-center justify-center gap-1">
                      <Link2 className="h-3 w-3" /> Connections
                    </p>
                  </div>
                </div>

                {/* Welcome message */}
                {eventDetail.welcomeMessage && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-sm font-medium mb-1.5">Welcome Message</p>
                      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                        {eventDetail.welcomeMessage}
                      </p>
                    </div>
                  </>
                )}

                {/* Registration Deadlines */}
                <Separator />
                <div>
                  <p className="text-sm font-medium mb-2">Registration Deadlines</p>
                  <DeadlineRow label="Exhibitor" iso={eventDetail.exhibitorRegistrationDeadline} />
                  <DeadlineRow label="NPS Project" iso={eventDetail.npsProjectRegistrationDeadline} />
                  <DeadlineRow label="General" iso={eventDetail.attendeeRegistrationDeadline} />
                </div>


          </div>
        </ScrollArea>
      ) : null}
    </div>
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <DesktopShell>
        <div className="h-full flex flex-col">
          <div className="px-6 py-5 border-b border-border bg-gradient-to-r from-background to-muted/30 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <CalendarDays className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Event Management</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Manage Converge@NPS events
                </p>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <ThreePanelLayout
              left={FiltersPanel}
              center={CenterPanel}
              right={RightPanel}
              leftWidth="260px"
              equalCenterRight
              connectedPanels={!!selectedEvent}
            />
          </div>
        </div>
      </DesktopShell>

      {/* Create / Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={(open) => !formSaving && setModalOpen(open)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editEventId ? 'Edit Event' : 'Create Event'}</DialogTitle>
          </DialogHeader>

          {formLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} className="space-y-4 pt-2">
              {formError && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200">
                  <AlertCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}

              <div>
                <Label htmlFor="ev-name" className="text-sm font-medium mb-1.5 block">Event Name *</Label>
                <Input
                  id="ev-name"
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  required
                  minLength={3}
                  maxLength={200}
                  placeholder="e.g., Converge@NPS 2026"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ev-dateStart" className="text-sm font-medium mb-1.5 block">Start Date *</Label>
                  <Input
                    id="ev-dateStart"
                    type="date"
                    value={formData.dateStart}
                    onChange={(e) => setFormData((p) => ({ ...p, dateStart: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="ev-dateEnd" className="text-sm font-medium mb-1.5 block">End Date *</Label>
                  <Input
                    id="ev-dateEnd"
                    type="date"
                    value={formData.dateEnd}
                    onChange={(e) => setFormData((p) => ({ ...p, dateEnd: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="ev-location" className="text-sm font-medium mb-1.5 block">Location *</Label>
                <Input
                  id="ev-location"
                  value={formData.location}
                  onChange={(e) => setFormData((p) => ({ ...p, location: e.target.value }))}
                  required
                  minLength={2}
                  maxLength={200}
                  placeholder="e.g., Naval Postgraduate School, Monterey, CA"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-1.5 block">Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData((p) => ({ ...p, status: v as EventStatus }))}
                >
                  <SelectTrigger className="capitalize">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
                {formData.status === 'active' && events.some((ev) => ev.status === 'active' && ev.id !== editEventId) && (
                  <p className="text-xs text-amber-600 mt-1">
                    Another event is currently active. You will be asked to archive it before this event can be activated.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="ev-welcome" className="text-sm font-medium mb-1.5 block">Welcome Message</Label>
                <Textarea
                  id="ev-welcome"
                  value={formData.welcomeMessage}
                  onChange={(e) => setFormData((p) => ({ ...p, welcomeMessage: e.target.value }))}
                  maxLength={2000}
                  rows={4}
                  placeholder="A welcome message shown to attendees when they log in..."
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.welcomeMessage.length} / 2000
                </p>
              </div>

              <Separator />

              <div>
                <Label className="text-sm font-medium block">Registration Deadlines</Label>
                <p className="text-xs text-muted-foreground mt-1 mb-3">
                  Set a date and time (your local timezone) per registration type. Leave the date blank to keep that type open. Leave the time blank to default to 11:59 PM.
                </p>

                <div className="space-y-3">
                  {[
                    { key: 'exhibitor', label: 'Exhibitor' },
                    { key: 'npsProject', label: 'NPS Project' },
                    { key: 'attendee', label: 'General' },
                  ].map(({ key, label }) => {
                    const dateField = `${key}DeadlineDate` as keyof EventFormData;
                    const timeField = `${key}DeadlineTime` as keyof EventFormData;
                    return (
                      <div key={key} className="grid grid-cols-[110px_minmax(0,1fr)_120px] gap-2 items-center">
                        <Label htmlFor={`ev-${key}-deadline-date`} className="text-xs font-medium">
                          {label}
                        </Label>
                        <Input
                          id={`ev-${key}-deadline-date`}
                          type="date"
                          value={formData[dateField]}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, [dateField]: e.target.value }))
                          }
                          aria-label={`${label} deadline date`}
                        />
                        <Input
                          id={`ev-${key}-deadline-time`}
                          type="time"
                          value={formData[timeField]}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, [timeField]: e.target.value }))
                          }
                          disabled={!formData[dateField]}
                          aria-label={`${label} deadline time`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={() => setModalOpen(false)} disabled={formSaving}>
                  Cancel
                </Button>
                <Button type="submit" disabled={formSaving} className="gap-2">
                  {formSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      {editEventId ? 'Update' : 'Create'}
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive-and-Proceed Confirmation — auto-archive existing active event */}
      <AlertDialog
        open={!!archiveAndProceedId}
        onOpenChange={(open) => !archiveAndProceedLoading && !open && setArchiveAndProceedId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Existing Active Event?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{events.find((ev) => ev.id === archiveAndProceedId)?.name}</strong> is currently
              the active event. It must be archived before another event can be activated. Archiving
              preserves all sessions and data under that event — it simply becomes read-only.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveAndProceedLoading}>Cancel</AlertDialogCancel>
            <Button onClick={handleArchiveAndProceed} disabled={archiveAndProceedLoading} className="gap-2">
              {archiveAndProceedLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Archiving...
                </>
              ) : (
                <>
                  <Archive className="h-4 w-4" />
                  Archive &amp; Proceed
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </>
  );
}

function DeadlineRow({ label, iso }: { label: string; iso: string | null | undefined }) {
  const now = Date.now();
  const time = iso ? new Date(iso).getTime() : NaN;
  const isClosed = !isNaN(time) && now > time;
  const display = iso && !isNaN(time)
    ? new Date(iso).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      })
    : 'Not set';

  return (
    <div className="flex items-center justify-between text-sm py-1">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span className={cn(isClosed && 'text-muted-foreground line-through')}>{display}</span>
        {isClosed && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 bg-muted text-muted-foreground border-border">
            Closed
          </Badge>
        )}
      </div>
    </div>
  );
}
